import * as Models from "../ts/rlAdmin.models";
import * as moment from "moment";

import { RLBaseService } from "./rl-base-service";
import { LocalCacheService } from "./local-cache-service";
import { PromiseCache } from "./promise-cache";

export class EntityConfigService extends RLBaseService {

    static $inject = ["$resource", "$q", "promiseCache", "localCacheService"];


    private fiveMins = moment.duration(5, 'minute');

    constructor(private $resource: ng.resource.IResourceService, private $q: ng.IQService, private promiseCache: PromiseCache, private localCacheService: LocalCacheService) {
        super($resource);
    }

    getCharacteristicTypes() {
        var key = "getcharacteristictypes";
        return this.localCacheService.getCachedOrDoCachePromise(key, () => this.promiseCache.get(key, () => this.toRLService<Models.ICharacteristicType>("/api/entityconfig/getcharacteristictypes").query().$promise), moment.duration(1, "hour"));
    }

    getRelatedCharacteristicTypes(charTypeID: number) {
        var data = { charTypeID: charTypeID };
        return this.toRLService<Models.ICharacteristicType>("/api/entityconfig/getrelatedcharacteristictypes").query(data).$promise;
    }

    getRelatedConfigurationCharacteristicTypes(charTypeID: number) {
        var data = { charTypeID: charTypeID };
        return this.toRLService<Models.ICharacteristicType>("/api/entityconfig/getrelatedconfigurationcharacteristictypes").query(data).$promise;
    }

    getRelatedConfigurationParentCharacteristicTypes(charTypeID: number) {
        var data = { charTypeID: charTypeID };
        return this.toRLService<Models.ICharacteristicType>("/api/entityconfig/getrelatedconfigurationparentcharacteristictypes").query(data).$promise;
    }

    getCharacteristicTemplates(charTypeID: number) {
        var data = { charTypeID: charTypeID };
        var key = "getcharacteristictemplates" + charTypeID;

        return this.promiseCache.get(key, () => this.toRLService<Models.ICharacteristicTemplate>("/api/entityconfig/getcharacteristictemplates").query(data).$promise);
    }

    getDefaultCharacteristicTemplate(charTypeID: number) {
        var data = { charTypeID: charTypeID };
        return this.toRLService<Models.ICharacteristicTemplate>("/api/entityconfig/getdefaultcharacteristictemplate").get(data).$promise;
    }

    getDefaultCharacteristicMetaDataCollection(charTypeID: number) {
        var data = { charTypeID: charTypeID };
        return this.toRLService<Models.ICharacteristicMetaDataCollection>("/api/entityconfig/getdefaultcharacteristicmetadatacollection").get(data).$promise;
    }

    getCharTypeTemplateAssociationList(charTypeID: number, templateID: number, useCache: boolean = true) {
        var data = {
            charTypeID: charTypeID,
            templateID: templateID
        };
        var fn = () => this.toRLService<Models.ITemplateAssociationCollection>("/api/entityconfig/getchartypetemplateassociationlist").get(data).$promise;
        if (useCache) {
            var key = `getCharTypeTemplateAssociationList_${charTypeID}_${templateID}`;
            return this.localCacheService.getCachedOrDoCachePromise(key, fn, this.fiveMins);
        } else {
            return fn();
        }
    }

    getCharTypeTemplateCharTypeAssociationList(charTypeID: number, templateID: number) {
        var data = {
            charTypeID: charTypeID,
            templateID: templateID
        };
        var key = `getCharTypeTemplateCharTypeAssociationList_${charTypeID}_${templateID}`;
        return this.localCacheService.getCachedOrDoCachePromise(key, () => this.toRLService<Models.ITemplateAssociation>("/api/entityconfig/getCharTypeTemplateCharTypeAssociationList").query(data).$promise, this.fiveMins);
    }

    updateTemplateRelationship(charTypeID: number, id: number, childCharTypeID: number, childTemplateID: number, relRecTypeID: number, selected: boolean) {
        var data = {
            charTypeID: charTypeID,
            id: id,
            childCharTypeID: childCharTypeID,
            childTemplateID: childTemplateID,
            relRecTypeID: relRecTypeID,
            selected: selected
        };
        var promise = this.toRLService<boolean>("/api/TemplateConfig/TemplateRelationship").post(data).$promise;
        promise.then(() => this.localCacheService.clearAll());
        return promise;
    }

    updateRightsRelTemplateRelationship(charTypeID: number, id: number, childCharTypeID: number, childTemplateID: number, relRecTypeID: number) {
        var data = {
            charTypeID: charTypeID,
            id: id,
            childCharTypeID: childCharTypeID,
            childTemplateID: childTemplateID,
            relRecTypeID: relRecTypeID
        };
        var promise = this.toRLService<boolean>("/api/TemplateConfig/UpdateTemplateRelationship").post(data).$promise;
        promise.then(() => this.localCacheService.clearAll());
        return promise;
    }

    getAvailableParentTemplates(templateID: number, charTypeID: number, parentCharTypeID: number) {
        var data = { templateID: templateID, charTypeID: charTypeID, parentCharTypeID: parentCharTypeID };
        return this.toRLService<Models.ICharacteristicTemplate>("/api/entity/getavailableparenttemplates").query(data).$promise;
    }

    getVisibilityOptions() {
        var values = [
            { key: 0, value: "Hidden" },
            { key: 1, value: "Internal Only" },
            { key: 2, value: "Both Internal & External" }
        ];
        return this.$q.when(values);
    }

    getCharDataTypes() {
        var values = [
            { key: 1, value: "4 Digit Year" },
            { key: 2, value: "Internet Address" },
            { key: 3, value: "Alpha Numeric Text" },
            { key: 4, value: "Number" },
            { key: 5, value: "Date" },
            { key: 6, value: "Email" },
            { key: 7, value: "Currency" },
            { key: 8, value: "Percentage" },
            { key: 9, value: "Password" },
            { key: 10, value: "Time Code" },
            { key: 11, value: "Credit Card" },
            { key: 12, value: "External Document" },
            { key: 13, value: "Checkbox" },
            { key: 14, value: "Money" },
            { key: 15, value: "Key Pair" }
        ];
        return this.$q.when(values);
    }

    getCharacteristicTemplateGroups(charTypeID: number) {
        var data = { charTypeID: charTypeID };
        return this.toRLService<Models.ICharacteristicTemplateGroup>("/api/entityconfig/getcharacteristictemplategroups").query(data).$promise;
    }

    getCharacteristicGroupList(charTypeID: number) {
        var data = { charTypeID: charTypeID };
        return this.toRLService<Models.ITemplateCharacteristicGroup>("/api/entityconfig/getcharacteristicgrouplist").query(data).$promise;
    }

    getCharacteristicMetaDataValueList(charTypeID: number, charID: number) {
        var data = { charTypeID: charTypeID, charID: charID };
        return this.toRLService<Models.ICharacteristicMetaDataValue>("/api/entityconfig/getcharacteristicmetadatavaluelist").query(data).$promise;
    }

    getCharacteristicTemplateMetaDataCollection(charTypeID: number, templateID: number) {
        var data = { charTypeID: charTypeID, templateID: templateID };
        return this.toRLService<Models.ICharacteristicMetaData>("/api/entityconfig/getcharacteristictemplatemetadatacollection").query(data).$promise;
    }

    getCharacteristicTemplate(charTypeID: number, templateID: number) {
        var data = {
            charTypeID: charTypeID,
            templateID: templateID
        };
        return this.toRLService<Models.ICharacteristicMetaDataCollection>("/api/entityconfig/getcharacteristictemplate").get(data).$promise;
    }

    createCharacteristic(charTypeID: number, label: string, tagLabel: string, systemIndicator: number, multipleIndicator: number, charValueSetID: string, dataTypeID: number, maxLength: number, reportIndicator: number, description: string) {
        var data = {
            charTypeID: charTypeID,
            label: label,
            tagLabel: tagLabel,
            systemIndicator: systemIndicator,
            multipleIndicator: multipleIndicator,
            charValueSetID: charValueSetID,
            dataTypeID: dataTypeID,
            maxLength: maxLength,
            reportIndicator: reportIndicator,
            description: description
        };
        var promise = this.toRLService<Models.ICharEditResponse>("/api/entityconfig/createcharacteristic").post(data).$promise;
        promise.then(() => this.localCacheService.clearAll());
        return promise;
    }

    updateCharacteristic(charTypeID: number, characteristicID: number, label: string, tagLabel: string, systemIndicator: number, multipleIndicator: number, charValueSetID: string, dataTypeID: number, maxLength: number, reportIndicator: number, description: string) {
        var data = {
            characteristicID: characteristicID,
            charTypeID: charTypeID,
            label: label,
            tagLabel: tagLabel,
            systemIndicator: systemIndicator,
            multipleIndicator: multipleIndicator,
            charValueSetID: charValueSetID,
            dataTypeID: dataTypeID,
            maxLength: maxLength,
            reportIndicator: reportIndicator,
            description: description
        };
        var promise = this.toRLService<Models.ICharEditResponse>("/api/entityconfig/updatecharacteristic").post(data).$promise;
        promise.then(() => this.localCacheService.clearAll());
        return promise;
    }

    getCharacteristic(charTypeID: number, characteristicID: number) {
        var data = {
            charTypeID: charTypeID,
            characteristicID: characteristicID
        };
        return this.toRLService<Models.ICharacteristic>("/api/entityconfig/getcharacteristic").get(data).$promise;
    }

    getCharacteristicList(charTypeID: number) {
        var data = {
            charTypeID: charTypeID
        };
        var key = `getCharacteristicList_${charTypeID}`;
        return this.localCacheService.getCachedOrDoCachePromise(key, () => this.toRLService<Models.ICharacteristic>("/api/entityconfig/getcharacteristiclist").query(data).$promise, this.fiveMins);
    }

    createCharacteristicMetaDataValue(charTypeID: number, charID: number, label: string, defInd: number) {
        var data = {
            charTypeID: charTypeID,
            charID: charID,
            label: label,
            defInd: defInd
        };
        return this.toRLService<number>("/api/entityconfig/createcharacteristicmetadatavalue").save(data).$promise;
    }

    updateCharacteristicMetaDataValue(charTypeID: number, charID: number, valueID: number, label: string, defInd: number, seqNumber: number) {
        var data = {
            charTypeID: charTypeID,
            charID: charID,
            valueID: valueID,
            label: label,
            defInd: defInd,
            seqNumber: seqNumber
        };
        return this.toRLService<boolean>("/api/entityconfig/updatecharacteristicmetadatavalue").save(data).$promise;
    }

    deleteCharacteristicMetaDataValue(charTypeID: number, charID: number, valueID: number) {
        var data = {
            charTypeID: charTypeID,
            charID: charID,
            valueID: valueID
        };
        return this.toRLService<boolean>("/api/entityconfig/deletecharacteristicmetadatavalue").post(data).$promise;
    }

    deleteAllCharacteristicMetaDataValue(charTypeID: number, charID: number) {
        var data = {
            charTypeID: charTypeID,
            charID: charID,
            valueID: -1
        };
        return this.toRLService<boolean>("/api/entityconfig/deleteallcharacteristicmetadatavalue").post(data).$promise;
    }

    getTemplateStatuses(charTypeID: number, templateID: number) {
        var data = {
            charTypeID: charTypeID,
            templateID: templateID
        };
        return this.toRLService<Models.IEntityStatus>("/api/entityconfig/gettemplatestatuses").query(data).$promise;
    }

    getLovList(charTypeID: number, charID: number, systemIndicator: number) {
        var data = {
            charTypeID: charTypeID,
            charID: charID,
            systemIndicator: systemIndicator
        };
        return this.toRLService<Models.ICharacteristicMetaDataValue>("/api/entityconfig/getlovlist").query(data).$promise;
    }

    getReportParameterValueList(paramID: number) {
        var data = {
            paramID: paramID
        };
        return this.toRLService<Models.ICharacteristicMetaDataValue>("/api/entityconfig/getreportparametervaluelist").query(data).$promise;
    }

    getReportDynamicParameterValueList(paramName: string) {
        var data = {
            paramName: paramName
        };
        return this.toRLService<{ params: Models.IDynamicReportLOV[]; totalRecords: number }>("/api/entityconfig/getreportdynamicparametervaluelist").get(data).$promise;
    }

    getCharacteristicValueSetList() {
        return this.toRLService<Models.ICharacteristicValueSet>("/api/entityconfig/getcharacteristicvaluesetlist").query().$promise;
    }

    getCharacteristicValueSet(charValueSetID: string) {
        var data = {
            charValueSetID: charValueSetID
        };
        return this.toRLService<Models.ICharacteristicValueSet>("/api/entityconfig/getcharacteristicvalueset").get(data).$promise;
    }

    createCharacteristicValueSet(valueSetLabel: string, valueSetDescription: string) {
        var data = {
            valueSetLabel: valueSetLabel,
            valueSetDescription: valueSetDescription
        };
        return this.toRLService<Models.ICreateCharValueSetResponse>("/api/entityconfig/createcharacteristicvalueset").post(data).$promise;
    }

    updateCharacteristicValueSet(charValueSetID: string, valueSetLabel: string, valueSetDescription: string) {
        var data = {
            charValueSetID: charValueSetID,
            valueSetLabel: valueSetLabel,
            valueSetDescription: valueSetDescription
        };
        return this.toRLService<void>("/api/entityconfig/updatecharacteristicvalueset").post(data).$promise;
    }

    deleteCharacteristicValueSet(charValueSetID: string) {
        var data = {
            charValueSetID: charValueSetID
        };
        return this.toRLService<void>("/api/entityconfig/deletecharacteristicvalueset").post(data).$promise;
    }

    getCharacteristicValueList(charValueSetID: string) {
        var data = {
            charValueSetID: charValueSetID
        };
        return this.toRLService<Models.ICharacteristicValue>("/api/entityconfig/getcharacteristicvaluelist").query(data).$promise;
    }

    getCharacteristicValueHierarchyList(charValueSourceID: string) {
        var data = {
            charValueSourceID: charValueSourceID
        };
        return this.toRLService<Models.ICharacteristicValueHierarchy>("/api/entityconfig/getcharacteristicvaluehierarchylist").query(data).$promise;
    }

    getCharacteristicValueSource(charValueSourceID: string) {
        var data = {
            charValueSourceID: charValueSourceID
        };
        return this.toRLService<Models.ICharacteristicValueSource>("/api/entityconfig/getcharacteristicvaluesource").get(data).$promise;
    }

    getCharacteristicValueSourceList(charValueSetID?: string) {
        var data = {};
        if (!_.isEmpty(charValueSetID)) {
            data = {
                charValueSetID: charValueSetID
            };
        }
        return this.toRLService<Models.ICharacteristicValueSource>("/api/entityconfig/getcharacteristicvaluesourcelist").query(data).$promise;
    }

    updateCharValueSourceHierarchy(charValueSourceID: string, charValueHierarchies: Models.ICharacteristicValueHierarchy[], charValues: Models.ICharacteristicValue[], charValueHierarchyValueGroupAssocs: { [key: string]: string[] }, charValueIDs: number[]) {
        var data = {
            charValueSourceID: charValueSourceID,
            charValueHierarchies: charValueHierarchies,
            charValues: charValues,
            charValueHierarchyValueGroupAssocs: charValueHierarchyValueGroupAssocs,
            charValueIDs: charValueIDs
        };
        return this.toRLService<Models.IJobResponse>("/api/entityconfig/updatecharvaluesourcehierarchy").post(data).$promise;
    }

    createCharacteristicValue(charValueSetID: string, charValueLabel: string, charValueDescription: string, xref: string, statusID: number) {
        var data = {
            charValueSetID: charValueSetID,
            charValueLabel: charValueLabel,
            charValueDescription: charValueDescription,
            xref: xref,
            statusID: statusID
        };
        return this.toRLService<Models.ICreateCharValueSetResponse>("/api/entityconfig/createcharacteristicvalue").post(data).$promise;
    }

    updateCharacteristicValue(charValueID: number, charValueSetID: string, charValueLabel: string, charValueDescription: string, xref: string, statusID: number) {
        var data = {
            charValueID: charValueID,
            charValueSetID: charValueSetID,
            charValueLabel: charValueLabel,
            charValueDescription: charValueDescription,
            xref: xref,
            statusID: statusID
        };
        return this.toRLService<Models.IJobResponse>("/api/entityconfig/updatecharacteristicvalue").post(data).$promise;
    }

    deleteCharacteristicValue(charValueID: number, charValueSetID: string) {
        var data = {
            charValueID: charValueID,
            charValueSetID: charValueSetID
        };
        return this.toRLService<void>("/api/entityconfig/deletecharacteristicvalue").post(data).$promise;
    }

    IsCharValSetCharValUsedInCharValSrcs(charValueID: number, charValueSetID: string) {
        var data = { charValueSetID: charValueSetID, charValID: charValueID };
        return this.toRLService<Models.IDataResponse<boolean>>("/api/entityconfig/ischarvalsetcharvalusedincharvalsrcs").get(data).$promise;
    }

    getCharValueGroupList(charValueSourceID: string) {
        var data = {
            charValueSourceID: charValueSourceID
        };
        return this.toRLService<Models.ICharacteristicValueGroup>("/api/entityconfig/getcharvaluegrouplist").query(data).$promise;
    }

    getCharValueGroup(charValueGroupID: string) {
        var data = {
            charValueGroupID: charValueGroupID
        };
        return this.toRLService<Models.ICharacteristicValueGroup>("/api/entityconfig/getcharvaluegroup").get(data).$promise;
    }

    getCharacteristicValueGroupCharacteristicValueList(charValueGroupID: string) {
        var data = {
            charValueGroupID: charValueGroupID
        };
        return this.toRLService<Models.ICharacteristicValueGroupCharacteristicValueAssociation>("/api/entityconfig/getcharacteristicvaluegroupcharacteristicvaluelist").query(data).$promise;
    }

    createCharValueGroup(charValueSourceID: string, charValueGroupLabel: string, charValueGroupDescription: string, assocCharValueIDs: number[]) {
        var data = {
            charValueGroupLabel: charValueGroupLabel,
            charValueGroupDescription: charValueGroupDescription,
            assocCharValueIDs: assocCharValueIDs,
            charValueSourceID: charValueSourceID
        };
        return this.toRLService<Models.ICreateCharValueGroupResponse>("/api/entityconfig/createcharvaluegroup").post(data).$promise;
    }

    updateCharValueGroup(charValueGroupID: string, charValueSourceID: string, charValueGroupLabel: string, charValueGroupDescription: string, assocCharValueIDs: number[]) {
        var data = {
            charValueGroupID: charValueGroupID,
            charValueSourceID: charValueSourceID,
            charValueGroupLabel: charValueGroupLabel,
            charValueGroupDescription: charValueGroupDescription,
            assocCharValueIDs: assocCharValueIDs
        };
        return this.toRLService<void>("/api/entityconfig/updatecharvaluegroup").post(data).$promise;
    }

    deleteCharValueGroup(charValueGroupID: string) {
        return this.toRLService<Models.IDataResponse<boolean>>("/api/entityconfig/deletecharvaluegroup").delete({ charValueGroupID: charValueGroupID }).$promise;
    }

    getCharacteristicValueGroupCharacteristicValueAssociationList(charValueGroupID: string) {
        var data = {
            charValueGroupID: charValueGroupID
        };
        return this.toRLService<Models.ICharacteristicValueGroupCharacteristicValueAssociation>("/api/entityconfig/getcharacteristicvaluegroupcharacteristicvalueassociationlist").query(data).$promise;
    }

    addAndAssociateCharacteristic(charTypeID: number, templateID: number, groupID: number, charMetaData: Models.ICharacteristicMetaData, permissions: _.Dictionary<Models.IAspNetSecurityPolicyPermission>, conditionalField: Models.IConditionalField) {
        var data = {
            charTypeID: charTypeID,
            templateID: templateID,
            groupID: groupID,
            charMetaData: charMetaData,
            permissions,
            conditionalField: conditionalField
        };
        var promise = this.toRLService<Models.ICharEditResponse>("/api/entityconfig/addandassociatecharacteristic").post(data).$promise;
        promise.then(() => this.localCacheService.clearAll());
        return promise;
    }

    getCharValueHierarchyValueGroupAssocList(charValueSourceID: string) {
        var data = {
            charValueSourceID: charValueSourceID
        };
        return this.toRLService<Models.ICharacteristicValueHierarchyValueGroupAssociation>("/api/entityconfig/getcharvaluehierarchyvaluegroupassoclist").query(data).$promise;
    }

    setCharacteristicValueHierarchyValueGroupAssociations(charValueSourceID: string, charValueHierarchyID: string, charValueGroupIDs: string[]) {
        var data = {
            charValueSourceID: charValueSourceID,
            charValueHierarchyID: charValueHierarchyID,
            charValueGroupIDs: charValueGroupIDs
        };
        return this.toRLService<void>("/api/entityconfig/setcharacteristicvaluehierarchyvaluegroupassociations").post(data).$promise;
    }

    updateCharacteristicTemplate(template: Models.ICharacteristicTemplate) {
        this.promiseCache.cache = {};
        return this.toRLService<boolean>("/api/templateconfig/updatetemplate").post(template).$promise;
    }

    updateTemplateSequenceNumber(charTypeID: number, templateID: number, direction: boolean) {
        this.promiseCache.cache = {};
        var data = {
            charTypeID: charTypeID,
            templateID: templateID,
            direction: direction
        };
        return this.toRLService<void>("/api/workflowconfig/UpdateTemplateSequenceNumber").post(data).$promise;
    }

    getAffectedEntitiesCount(request: Models.ICharacteristicTemplate) {
        return this.toRLService<Models.IDataResponse<number>>("/api/entityconfig/getaffectedentitiescount").post(request).$promise;
    }

    bulkUpdateRelatedEntitiesByTemplate(request: Models.ICharacteristicTemplate) {
        return this.toRLService<void>("/api/entityconfig/bulkUpdateAffectedRecords").post(request).$promise;
    }

    getTemplateParties(charTypeID: number, templateID: number) {
        var data = { charTypeID: charTypeID, templateID: templateID };
        return this.toRLService<Models.IParty>("/api/entityconfig/gettemplatepartylist").query(data).$promise;
    }

    getCharValueSourceUsageIn(charValueSourceID: string) {
        var data = { charValueSourceID: charValueSourceID };
        return this.toRLService<Models.ICharValueSourceUsageIn>("/api/entityconfig/getcharacteristicvaluesourceusagein").query(data).$promise;
    }

    getCharValueSetUsageIn(charValueSetID: string) {
        var data = { charValueSetID: charValueSetID };
        return this.toRLService<Models.ICharValueSetUsageIn>("/api/entityconfig/getcharacteristicvaluesetusagein").query(data).$promise;
    }

    getCharacteristicUsageIn(charTypeID: number, characteristicID: number) {
        var data = { charTypeID: charTypeID, characteristicID: characteristicID };
        return this.toRLService<Models.ICharacteristicUsageIn>("/api/entityconfig/getcharacteristicusagein").query(data).$promise;
    }

    getCharacteristicValueSourceCharacteristicCount(charValueSourceID: string, charValID: number) {
        var data = { charValueSourceID: charValueSourceID, charValID: charValID };
        return this.toRLService<Models.IDataResponse<number>>("/api/entityconfig/getcharacteristicvaluecharacteristiccount").get(data).$promise;
    }

    getEntityListCountByTemplate(charTypeID: number, templateID: number) {
        var data = {
            charTypeID: charTypeID,
            templateID: templateID
        };
        return this.toRLService<Models.IDataResponse<number>>("/api/entityconfig/getentitylistcountbytemplate").get(data).$promise;
    }

    copyCharacteristicTemplate(charTypeID: number, templateID: number) {
        var data = {
            charTypeID: charTypeID,
            templateID: templateID
        };
        this.promiseCache.cache = {};
        return this.toRLService<Models.IDataResponse<boolean>>("/api/entityconfig/CopyCharacteristicTemplate").post(data).$promise;
    }

    getUniqueTitleIndicator(charTypeID: number, characteristicID: number, templateID: number) {
        var data = {
            charTypeID: charTypeID,
            characteristicID: characteristicID,
            templateID: templateID
        };
        return this.toRLService<Models.IDataResponse<boolean>>("/api/entityconfig/getUniqueTitleIndicator").get(data).$promise;
    }

    getAlertNofitications(charTypeID: number, templateID: number) {
        var data = {
            charTypeID: charTypeID,
            templateID: templateID,
        };
        return this.toRLService<Models.IWFRoleNotification>("/api/entityconfig/GetAlertNotifications").query(data).$promise;
    }

    getCharacteristicDataCount(charTypeID: number, characteristicID: number) {
        var data = {
            charTypeID: charTypeID,
            characteristicID: characteristicID
        };
        return this.toRLService<Models.IDataResponse<number>>("/api/entityconfig/GetCharacteristicDataCount").get(data).$promise;
    }

    exportListOfValuesHierarchyToCSV(charValueSetID: string, charValueSourceID: string, listTitle: string) {
        const data = {
            charValueSetID,
            charValueSourceID,
            listTitle
        };
        return this.toRLService<Models.IJobResponse>("/api/entityconfig/ExportListOfValuesHierarchyToCSV").get(data).$promise;
    }

    getCatalogHierarchy(recIDs: string) {
        var data = {
            recIDs: recIDs
        };
        return this.toRLService<Models.IDataResponse<Models.ICatalogHierarchy[]>>("/api/entityconfig/getCatalogHierarchy").post(data).$promise;
    }

    getAssociatedList(charTypeID: number, recIDs: string, partyIDs: string, showOtherCharTypeIDs: string) {
        var data = {
            charTypeID: charTypeID,
            recIDs: recIDs,
            partyRecIDs: partyIDs,
            otherAssociatedCharTypeIDs: showOtherCharTypeIDs
        };
        return this.toRLService<Models.IDataResponse<Models.IAssociatedItem[]>>("/api/entityconfig/getAssociatedList").post(data).$promise;
    }

    getParties() {
        return this.toRLService<Models.IDataResponse<Models.IParty[]>>("/api/entityconfig/getParties").get().$promise;
    }

    getRecordAssociationCharacteristicTemplateRequired(charTypeID: number, templateID: number, childCharTypeID: number) {
        var data = {
            charTypeID: charTypeID,
            templateID: templateID,
            childCharTypeID: childCharTypeID
        }

        return this.toRLService<Models.IRecordAssociationCharacteristicTemplateRequired>("/api/entityconfig/GetRecordAssociationCharacteristicTemplateRequired").get(data).$promise;
    }

    createRecordAssociationConfigurationRequiredEntry(charTypeID: number, templateID: number, childCharTypeID: number) {
        var data = {
            charTypeID: charTypeID,
            templateID: templateID,
            childCharTypeID: childCharTypeID
        }

        return this.toRLService<Models.IRecordAssociationCharacteristicTemplateRequired[]>("/api/entityconfig/CreateRecordAssociationConfigurationRequiredEntry").get(data).$promise;
    }

    deleteRecordAssociationConfigurationRequiredEntry(charTypeID: number, templateID: number, childCharTypeID: number) {
        var data = {
            charTypeID: charTypeID,
            templateID: templateID,
            childCharTypeID: childCharTypeID
        }

        return this.toRLService<Models.IRecordAssociationCharacteristicTemplateRequired[]>("/api/entityconfig/DeleteRecordAssociationConfigurationRequiredEntry").get(data).$promise;
    }

    getConditionalField(charTypeID: number, templateID: number, charID: number) {
        var data = {
            charTypeID: charTypeID,
            templateID: templateID,
            charID: charID
        }

        return this.toRLService<Models.IDataResponse<Models.IConditionalField>>("/api/entityconfig/getconditionalfield").get(data).$promise;
    }

    createConditionalField(conditionalField: Models.IConditionalField) {
        return this.toRLService<Models.IDataResponse<void>>("/api/entityconfig/createconditionalfield").post(conditionalField).$promise;
    }

    deleteConditionalField(conditionalField: Models.IConditionalField) {
        return this.toRLService<Models.IDataResponse<void>>("/api/entityconfig/deleteconditionalfield").post(conditionalField).$promise;
    }

    updateConditionalField(conditionalField: Models.IConditionalField) {
        return this.toRLService<Models.IDataResponse<void>>("/api/entityconfig/updateconditionalfield").post(conditionalField).$promise;
    }

    getConditionalFieldTypes() {
        return this.toRLService<Models.IKeyValuePair<string, string>>("/api/entityconfig/getconditionalfieldtypes").query().$promise;
    }

    getMockRightsByTemplate(mockRecordID: number, start: number, rowCount: number) {
        var data = {
            mockRecordID: mockRecordID,
            start: start,
            rowCount: rowCount
        }

        return this.toRLService<Models.IDataResponse<Models.IMockRelatedEntityResponse>>("/api/entityconfig/getmockrightsbytemplate").post(data).$promise;
    }

    getMockTableTriggerModelsByTemplate(mockRecordID: number, start: number, rowCount: number) {
        var data = {
            mockRecordID: mockRecordID,
            start: start,
            rowCount: rowCount
        }

        return this.toRLService<Models.IDataResponse<Models.IMockRelatedEntityResponse>>("/api/entityconfig/getmocktabletriggermodelsbytemplate").post(data).$promise;
    }

    getRecordAssociationParentChildTemplateList(charTypeID: number, recID: number) {
        var data = {
            charTypeID: charTypeID,
            recID: recID
        };
        return this.toRLService<Models.IRecordAssocationParentChildTemplateAssociation>("/api/entityconfig/getRecordAssociationParentChildTemplateList").query(data).$promise;
    }

    getRightsDimensions() {
        return this.toRLService<{ characteristics: _.Dictionary<Models.ICharacteristic>, lovs: _.Dictionary<Models.ICharLovMetaData> }>("/api/entityconfig/getrightsdimensions").get().$promise;
    }
}
